/**
 * Auto-Citation Generator
 * Generates formatted citations in MLA, APA, Chicago, Harvard, Vancouver + BibTeX
 * from research paper metadata.
 */

/* ─── Helper: parse author name ──────────────────────────── */
function parseAuthor(name) {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return { first: '', last: parts[0], initials: '' };
  // Handle "D. Mitchell" (initial + last) or "Daniel Mitchell"
  const last = parts[parts.length - 1];
  const firstParts = parts.slice(0, -1);
  const first = firstParts.join(' ');
  const initials = firstParts.map(p => p.replace(/\.$/, '').charAt(0).toUpperCase() + '.').join(' ');
  return { first, last, initials };
}

function getYear(dateStr) {
  if (!dateStr) return new Date().getFullYear().toString();
  return new Date(dateStr).getFullYear().toString();
}

function getMonth(dateStr) {
  if (!dateStr) return '';
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return months[new Date(dateStr).getMonth()];
}

/* ─── MLA 9th Edition ───────────────────────────────────── */
export function generateMLA(paper) {
  const authors = (paper.authors || []).map(parseAuthor);
  const year = getYear(paper.publishedDate);
  const venue = paper.venue || 'Vector Labs Reports';

  let authorStr = '';
  if (authors.length === 0) {
    authorStr = '';
  } else if (authors.length === 1) {
    authorStr = `${authors[0].last}, ${authors[0].first}`;
  } else if (authors.length === 2) {
    authorStr = `${authors[0].last}, ${authors[0].first}, and ${authors[1].first} ${authors[1].last}`;
  } else {
    authorStr = `${authors[0].last}, ${authors[0].first}, et al`;
  }

  let citation = `${authorStr}. "${paper.title}." `;
  citation += `*${venue}*`;
  if (paper.volume) citation += ` ${paper.volume}`;
  if (paper.issue) citation += `.${paper.issue}`;
  citation += ` (${year})`;
  if (paper.pages) citation += `: ${paper.pages}`;
  citation += '.';
  if (paper.doi) citation += ` doi:${paper.doi}.`;

  return citation;
}

/* ─── APA 7th Edition ───────────────────────────────────── */
export function generateAPA(paper) {
  const authors = (paper.authors || []).map(parseAuthor);
  const year = getYear(paper.publishedDate);
  const venue = paper.venue || 'Vector Labs Reports';

  let authorStr = '';
  if (authors.length === 1) {
    authorStr = `${authors[0].last}, ${authors[0].initials}`;
  } else if (authors.length === 2) {
    authorStr = `${authors[0].last}, ${authors[0].initials}, & ${authors[1].last}, ${authors[1].initials}`;
  } else if (authors.length <= 20) {
    const parts = authors.map((a, i) => {
      if (i === authors.length - 1) return `& ${a.last}, ${a.initials}`;
      return `${a.last}, ${a.initials}`;
    });
    authorStr = parts.join(', ').replace(', &', ', &');
  } else {
    const first19 = authors.slice(0, 19).map(a => `${a.last}, ${a.initials}`).join(', ');
    const last = authors[authors.length - 1];
    authorStr = `${first19}, ... ${last.last}, ${last.initials}`;
  }

  let citation = `${authorStr} (${year}). ${paper.title}. `;
  citation += `*${venue}*`;
  if (paper.volume) citation += `, *${paper.volume}*`;
  if (paper.issue) citation += `(${paper.issue})`;
  if (paper.pages) citation += `, ${paper.pages}`;
  citation += '.';
  if (paper.doi) citation += ` https://doi.org/${paper.doi}`;

  return citation;
}

/* ─── Chicago 17th (Author-Date) ────────────────────────── */
export function generateChicago(paper) {
  const authors = (paper.authors || []).map(parseAuthor);
  const year = getYear(paper.publishedDate);
  const venue = paper.venue || 'Vector Labs Reports';

  let authorStr = '';
  if (authors.length === 1) {
    authorStr = `${authors[0].last}, ${authors[0].first}`;
  } else if (authors.length === 2) {
    authorStr = `${authors[0].last}, ${authors[0].first}, and ${authors[1].first} ${authors[1].last}`;
  } else if (authors.length === 3) {
    authorStr = `${authors[0].last}, ${authors[0].first}, ${authors[1].first} ${authors[1].last}, and ${authors[2].first} ${authors[2].last}`;
  } else {
    const allButLast = authors.slice(0, -1).map((a, i) =>
      i === 0 ? `${a.last}, ${a.first}` : `${a.first} ${a.last}`
    ).join(', ');
    authorStr = `${allButLast}, and ${authors[authors.length - 1].first} ${authors[authors.length - 1].last}`;
  }

  let citation = `${authorStr}. "${paper.title}." `;
  citation += `*${venue}*`;
  if (paper.volume) citation += ` ${paper.volume}`;
  if (paper.issue) citation += `, no. ${paper.issue}`;
  citation += ` (${year})`;
  if (paper.pages) citation += `: ${paper.pages}`;
  citation += '.';
  if (paper.doi) citation += ` https://doi.org/${paper.doi}.`;

  return citation;
}

/* ─── Harvard ───────────────────────────────────────────── */
export function generateHarvard(paper) {
  const authors = (paper.authors || []).map(parseAuthor);
  const year = getYear(paper.publishedDate);
  const venue = paper.venue || 'Vector Labs Reports';

  let authorStr = '';
  if (authors.length === 1) {
    authorStr = `${authors[0].last}, ${authors[0].initials}`;
  } else if (authors.length === 2) {
    authorStr = `${authors[0].last}, ${authors[0].initials} and ${authors[1].last}, ${authors[1].initials}`;
  } else {
    const allButLast = authors.slice(0, -1).map(a => `${a.last}, ${a.initials}`).join(', ');
    authorStr = `${allButLast} and ${authors[authors.length - 1].last}, ${authors[authors.length - 1].initials}`;
  }

  let citation = `${authorStr}, ${year}. ${paper.title}. `;
  citation += `*${venue}*`;
  if (paper.volume) citation += `, ${paper.volume}`;
  if (paper.issue) citation += `(${paper.issue})`;
  if (paper.pages) citation += `, pp.${paper.pages}`;
  citation += '.';
  if (paper.doi) citation += ` doi:${paper.doi}.`;

  return citation;
}

/* ─── Vancouver ─────────────────────────────────────────── */
export function generateVancouver(paper) {
  const authors = (paper.authors || []).map(parseAuthor);
  const year = getYear(paper.publishedDate);
  const month = getMonth(paper.publishedDate);
  const venue = paper.venue || 'Vector Labs Reports';

  let authorStr = '';
  if (authors.length <= 6) {
    authorStr = authors.map(a => `${a.last} ${a.initials.replace(/\.\s/g, '').replace(/\./g, '')}`).join(', ');
  } else {
    const first6 = authors.slice(0, 6).map(a => `${a.last} ${a.initials.replace(/\.\s/g, '').replace(/\./g, '')}`).join(', ');
    authorStr = `${first6}, et al`;
  }

  let citation = `${authorStr}. ${paper.title}. ${venue}. ${year}`;
  if (month) citation += ` ${month}`;
  if (paper.volume) citation += `;${paper.volume}`;
  if (paper.issue) citation += `(${paper.issue})`;
  if (paper.pages) citation += `:${paper.pages}`;
  citation += '.';
  if (paper.doi) citation += ` doi:${paper.doi}.`;

  return citation;
}

/* ─── BibTeX ────────────────────────────────────────────── */
export function generateBibTeX(paper) {
  const authors = (paper.authors || []).map(parseAuthor);
  const year = getYear(paper.publishedDate);
  const venue = paper.venue || 'Vector Labs Reports';

  // Generate a cite key: firstAuthorLast + year + firstWordOfTitle
  const citeKey = (authors[0]?.last || 'unknown').toLowerCase() +
    year +
    (paper.title.split(/\s+/)[0] || '').toLowerCase().replace(/[^a-z]/g, '');

  const authorStr = authors.map(a => `${a.last}, ${a.first}`).join(' and ');

  let bib = `@article{${citeKey},\n`;
  bib += `  title     = {${paper.title}},\n`;
  bib += `  author    = {${authorStr}},\n`;
  bib += `  journal   = {${venue}},\n`;
  bib += `  year      = {${year}},\n`;
  if (paper.volume) bib += `  volume    = {${paper.volume}},\n`;
  if (paper.issue) bib += `  number    = {${paper.issue}},\n`;
  if (paper.pages) bib += `  pages     = {${paper.pages}},\n`;
  if (paper.doi) bib += `  doi       = {${paper.doi}},\n`;
  bib += `  abstract  = {${(paper.abstract || '').replace(/[{}]/g, '')}}\n`;
  bib += `}`;

  return bib;
}

/* ─── Generate all citation formats ─────────────────────── */
export function generateAllCitations(paper) {
  return {
    mla: generateMLA(paper),
    apa: generateAPA(paper),
    chicago: generateChicago(paper),
    harvard: generateHarvard(paper),
    vancouver: generateVancouver(paper),
    bibtex: generateBibTeX(paper),
  };
}

/* ─── Citation format display labels ────────────────────── */
export const CITATION_FORMATS = [
  { key: 'mla', label: 'MLA' },
  { key: 'apa', label: 'APA' },
  { key: 'chicago', label: 'Chicago' },
  { key: 'harvard', label: 'Harvard' },
  { key: 'vancouver', label: 'Vancouver' },
  { key: 'bibtex', label: 'BibTeX' },
];
